/**
 * Shared scoring logic for pinewood derby leaderboards.
 * Used by public leaderboard, TV display, awards, and finals seeding.
 */

export type CarScore = {
	car: {
		id: number;
		carNumber: number;
		carName: string | null;
		driverName: string;
		avatarUrl: string | null;
		photoUrl: string | null;
		division: string | null;
	};
	bestTime: number | null;
	avgTime: number | null;
	runCount: number;
	score: number | null;
	/** For points scoring: display string like "12 pts" instead of time */
	scoreDisplay?: string;
};

type RunData = {
	carId: number;
	elapsedTimeMs: number | null;
	lane?: number;
	heatId?: number | null;
};

type HeatData = {
	id: number;
	status: string;
	phase?: string;
};

type HeatAssignmentData = {
	heatId: number;
	lane: number;
	carId: number;
};

/**
 * Compute scores for all cars using the given scoring method.
 * Supports: best, average, drop_worst, points.
 *
 * For "points" mode, pass heats and assignments to calculate placement points.
 * Points: 1st in heat = 1pt, 2nd = 2pt, etc. DNF = laneCount + 1. Lower total wins.
 */
export function computeScores(
	eventCars: CarScore["car"][],
	eventRuns: RunData[],
	scoringMethod: string,
	options?: {
		heats?: HeatData[];
		assignments?: HeatAssignmentData[];
		laneCount?: number;
		/** Filter runs to only those linked to heats with this phase */
		phase?: "qualifying" | "finals";
	},
): CarScore[] {
	// Filter runs by phase if specified
	let filteredRuns = eventRuns;
	if (options?.phase && options?.heats) {
		const phaseHeatIds = new Set(
			options.heats.filter((h) => h.phase === options.phase).map((h) => h.id),
		);
		filteredRuns = eventRuns.filter(
			(r) => r.heatId != null && phaseHeatIds.has(r.heatId),
		);
	}

	// For points scoring, compute per-heat placements
	if (scoringMethod === "points" && options?.heats && options?.assignments) {
		return computePointsScores(
			eventCars,
			filteredRuns,
			options.heats,
			options.assignments,
			options.laneCount ?? 4,
		);
	}

	return eventCars
		.map((car) => {
			const carRuns = filteredRuns
				.filter((r) => r.carId === car.id && r.elapsedTimeMs != null)
				.map((r) => r.elapsedTimeMs as number);

			const bestTime = carRuns.length > 0 ? Math.min(...carRuns) : null;
			const avgTime =
				carRuns.length > 0
					? carRuns.reduce((a, b) => a + b, 0) / carRuns.length
					: null;

			let score: number | null = null;
			if (scoringMethod === "best") {
				score = bestTime;
			} else if (scoringMethod === "average") {
				score = avgTime;
			} else if (scoringMethod === "drop_worst" && carRuns.length > 1) {
				const sorted = [...carRuns].sort((a, b) => a - b);
				sorted.pop();
				score = sorted.reduce((a, b) => a + b, 0) / sorted.length;
			} else {
				score = bestTime;
			}

			return { car, bestTime, avgTime, runCount: carRuns.length, score };
		})
		.sort(compareScores);
}

/**
 * Points scoring: rank cars by total placement points across completed heats.
 * 1st in heat = 1pt, 2nd = 2pt, etc. DNF = laneCount + 1.
 */
function computePointsScores(
	eventCars: CarScore["car"][],
	eventRuns: RunData[],
	eventHeats: HeatData[],
	heatAssignments: HeatAssignmentData[],
	laneCount: number,
): CarScore[] {
	const completedHeatIds = new Set(
		eventHeats.filter((h) => h.status === "complete").map((h) => h.id),
	);

	// Build a map of heatId -> carId -> best time in that heat
	const heatCarTimes = new Map<number, Map<number, number | null>>();
	for (const heatId of completedHeatIds) {
		const carsInHeat = heatAssignments
			.filter((a) => a.heatId === heatId)
			.map((a) => a.carId);

		const carTimes = new Map<number, number | null>();
		for (const carId of carsInHeat) {
			// Find the valid run for this car in this heat's lane
			const assignment = heatAssignments.find(
				(a) => a.heatId === heatId && a.carId === carId,
			);
			if (!assignment) continue;

			const run = eventRuns.find(
				(r) =>
					r.carId === carId &&
					r.lane === assignment.lane &&
					r.heatId === heatId &&
					r.elapsedTimeMs != null,
			);
			carTimes.set(carId, run?.elapsedTimeMs ?? null);
		}
		heatCarTimes.set(heatId, carTimes);
	}

	// Calculate points per car
	const pointTotals = new Map<number, number>();
	const heatCounts = new Map<number, number>();

	for (const [, carTimes] of heatCarTimes) {
		// Rank cars in this heat by time
		const entries = [...carTimes.entries()].sort((a, b) => {
			if (a[1] == null && b[1] == null) return 0;
			if (a[1] == null) return 1;
			if (b[1] == null) return -1;
			return a[1] - b[1];
		});

		entries.forEach(([carId, time], idx) => {
			const points = time != null ? idx + 1 : laneCount + 1;
			pointTotals.set(carId, (pointTotals.get(carId) || 0) + points);
			heatCounts.set(carId, (heatCounts.get(carId) || 0) + 1);
		});
	}

	return eventCars
		.map((car) => {
			const carRuns = eventRuns
				.filter((r) => r.carId === car.id && r.elapsedTimeMs != null)
				.map((r) => r.elapsedTimeMs as number);

			const bestTime = carRuns.length > 0 ? Math.min(...carRuns) : null;
			const avgTime =
				carRuns.length > 0
					? carRuns.reduce((a, b) => a + b, 0) / carRuns.length
					: null;

			const points = pointTotals.get(car.id) ?? null;
			const heatsRaced = heatCounts.get(car.id) ?? 0;

			return {
				car,
				bestTime,
				avgTime,
				runCount: carRuns.length,
				score: points,
				scoreDisplay:
					points != null ? `${points} pts (${heatsRaced}h)` : undefined,
			};
		})
		.sort(compareScores);
}

/**
 * Compare two car scores for sorting.
 * Primary: score (lower wins, null last)
 * Tiebreaker 1: average time (lower wins)
 * Tiebreaker 2: car number (lower wins)
 */
export function compareScores(a: CarScore, b: CarScore): number {
	// Nulls go last
	if (a.score == null && b.score == null)
		return a.car.carNumber - b.car.carNumber;
	if (a.score == null) return 1;
	if (b.score == null) return -1;

	// Primary: score
	if (a.score !== b.score) return a.score - b.score;

	// Tiebreaker 1: average time
	if (a.avgTime != null && b.avgTime != null && a.avgTime !== b.avgTime) {
		return a.avgTime - b.avgTime;
	}

	// Tiebreaker 2: car number
	return a.car.carNumber - b.car.carNumber;
}

/**
 * Format a score for display. If scoreDisplay is set (points mode), use that.
 * Otherwise format as time.
 */
export function formatScore(entry: CarScore): string {
	if (entry.scoreDisplay) return entry.scoreDisplay;
	if (entry.score == null) return "--";
	return `${(entry.score / 1000).toFixed(3)}s`;
}
